import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { requestPersistence } from '../../app/persistence';
import { enterLocalMode } from '../../lib/localMode';
import { readStorage, writeStorage } from '../../lib/safeStorage';
import { FLAG_ON, StorageKey } from '../../lib/storageKeys';
import { useSyncConfigured } from '../../hooks/useSyncStatus';
import { toastError, toastNotice } from '../../shell/toastStore';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import styles from './Welcome.module.css';

/**
 * The secondary entry at the bottom of the welcome page: use the app in this browser only. The risk
 * is stated in a confirmation dialog first (ui-spec §7); confirming sets the local-mode flag, which lets the
 * welcome gate open the app, and asks the browser to keep the local database from being evicted.
 */
export function LocalModeEntry() {
  const { t } = useTranslation();
  const [asking, setAsking] = useState(false);
  const configured = useSyncConfigured();

  const start = (): void => {
    setAsking(false);
    if (!enterLocalMode()) {
      // The choice could not be stored, so the app would show this page again after a reload.
      toastError(t('welcome.localMode.storageBlocked'));
      return;
    }
    void requestPersistence().then((persistent) => {
      // Same notice as at start-up (main.tsx): data is only in this browser, so eviction matters most here.
      if (persistent || readStorage(StorageKey.PersistNoticeDismissed) === FLAG_ON) return;
      toastNotice(t('persist.denied'), () => writeStorage(StorageKey.PersistNoticeDismissed, FLAG_ON));
    });
  };

  // Local mode and sync exclude each other: a device that has sync configured never offers it.
  if (configured) return null;
  return (
    <div className={styles.localEntry}>
      <button
        type="button"
        className={styles.linkButton}
        onClick={() => setAsking(true)}
        data-testid="welcome-local-only"
      >
        {t('welcome.localMode.entry')}
      </button>
      {asking ? (
        <ConfirmDialog
          title={t('welcome.localMode.title')}
          confirmLabel={t('welcome.localMode.confirm')}
          cancelLabel={t('welcome.localMode.back')}
          onConfirm={start}
          onCancel={() => setAsking(false)}
          testId="local-mode-dialog"
        >
          <p>{t('welcome.localMode.body')}</p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
